import React, { useEffect, useMemo, useState } from 'react';
import { ChainStep, Pipeline } from '../../types';
import { useNavigate } from 'react-router-dom';
import VisualChainBuilder from '../../components/VisualChainBuilder';
import { useTheme } from '../../ThemeContext';
import { apiGet, apiPost } from '../../utils/apiClient';

export default function ChainEditorPage() {
  const { theme } = useTheme();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [continueAfterFailure, setContinueAfterFailure] = useState(false);
  const [stepsText, setStepsText] = useState('[\n  { "pipeline_id": "", "depends_on": [] }\n]');
  const [steps, setSteps] = useState<ChainStep[]>([]);
  const [activeTab, setActiveTab] = useState<'visual' | 'json'>('visual');
  const [pipelines, setPipelines] = useState<Pipeline[]>([]);
  const [loadingPipes, setLoadingPipes] = useState<boolean>(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const load = async () => {
      setLoadingPipes(true);
      try {
        const res = await apiGet('/api/pipelines');
        if (res.ok) {
          const data = await res.json();
          setPipelines(Array.isArray(data) ? data : []);
        }
      } catch {}
      setLoadingPipes(false);
    };
    load();
  }, []);

  // When switching to JSON tab, serialize current steps to JSON for editing
  useEffect(() => {
    if (activeTab === 'json') {
      setStepsText(JSON.stringify(steps, null, 2));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const switchTab = (tab: 'visual' | 'json') => {
    if (tab === 'visual' && activeTab === 'json') {
      // try apply JSON to steps
      try {
        const parsed = JSON.parse(stepsText);
        if (Array.isArray(parsed)) setSteps(parsed);
      } catch {}
    }
    setActiveTab(tab);
  };

  const submitPayloadSteps = (): ChainStep[] | null => {
    if (activeTab === 'visual') return steps;
    try {
      const parsed = JSON.parse(stepsText);
      if (!Array.isArray(parsed)) throw new Error('Steps must be an array');
      return parsed;
    } catch (e: any) {
      setError(e?.message || 'Invalid JSON in steps');
      return null;
    }
  };

  const handleSubmit = async () => {
    setError(null);
    const payloadSteps = submitPayloadSteps();
    if (!payloadSteps) return;
    if (!name.trim()) {
      setError('Name is required');
      return;
    }
    setSaving(true);
    try {
      const res = await apiPost('/api/chains', { name, description: description || null, steps: payloadSteps, continue_after_failure: continueAfterFailure });
      if (!res.ok) throw new Error(`Failed to create chain (${res.status})`);
      const data = await res.json();
      navigate(`/chains/${data.chain_id}`);
    } catch (e: any) {
      setError(e?.message || 'Failed to save chain');
    } finally {
      setSaving(false);
    }
  };

  const builder = (
    <div style={{ marginTop: 8 }}>
      {loadingPipes ? (
        <div style={{ color: theme.textSecondary }}>Loading pipelines…</div>
      ) : (
        <VisualChainBuilder
          pipelines={pipelines}
          value={steps}
          onChange={setSteps}
          readOnly={false}
          style={{ maxWidth: 1200 }}
        />
      )}
    </div>
  );

  const jsonEditor = (
    <label>
      <div style={{ fontWeight: 600, marginBottom: 6, color: theme.text }}>Steps (JSON)</div>
      <textarea value={stepsText} onChange={e => setStepsText(e.target.value)} rows={12} spellCheck={false} style={{ width: '100%', fontFamily: 'monospace', background: theme.inputBackground, color: theme.text, padding: 12, border: '1px solid ${theme.inputBorder}', borderRadius: 8 }} />
      <div style={{ color: theme.textSecondary, fontSize: 12, marginTop: 6 }}>Example: [ {`{ "pipeline_id": "A", "depends_on": [] }`}, {`{ "pipeline_id": "B", "depends_on": ["A"] }`} ]</div>
    </label>
  );

  return (
    <div>
      <h2 style={{ marginTop: 0, color: theme.text }}>Create Chain</h2>
      <p style={{ color: theme.textSecondary }}>Use the visual builder to link pipelines or switch to JSON to edit raw steps.</p>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 12 }}>
        <label>
          <div style={{ fontWeight: 600, marginBottom: 6, color: theme.text }}>Name</div>
          <input value={name} onChange={e => setName(e.target.value)} placeholder="e.g., Daily Customer Sync" style={{ width: '100%', padding: '10px 12px', border: '1px solid ${theme.inputBorder}', borderRadius: 8 }} />
        </label>
        <label>
          <div style={{ fontWeight: 600, marginBottom: 6, color: theme.text }}>Description</div>
          <input value={description} onChange={e => setDescription(e.target.value)} placeholder="Optional" style={{ width: '100%', padding: '10px 12px', border: '1px solid ${theme.inputBorder}', borderRadius: 8 }} />
        </label>

        <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
          <input 
            type="checkbox" 
            checked={continueAfterFailure} 
            onChange={e => setContinueAfterFailure(e.target.checked)}
            style={{ cursor: 'pointer' }}
          />
          <span style={{ color: theme.text }}>Continue after failure</span>
          <span style={{ color: theme.textSecondary, fontSize: 13 }}>(Execute remaining steps even if a step fails)</span>
        </label>

        {/* Tabs */}
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={() => switchTab('visual')} style={{ padding: '8px 10px', borderRadius: 8, border: `1px solid ${theme.inputBorder}`, background: activeTab === 'visual' ? theme.buttonBackground : theme.cardBackground, color: activeTab === 'visual' ? theme.buttonText : theme.text, fontWeight: 700 }}>Visual Builder</button>
          <button onClick={() => switchTab('json')} style={{ padding: '8px 10px', borderRadius: 8, border: `1px solid ${theme.inputBorder}`, background: activeTab === 'json' ? theme.buttonBackground : theme.cardBackground, color: activeTab === 'json' ? theme.buttonText : theme.text, fontWeight: 700 }}>JSON Editor</button>
        </div>

        {activeTab === 'visual' ? builder : jsonEditor}

        {error && <div style={{ color: theme.error, padding: 8, background: theme.errorLight, border: '1px solid ${theme.error}', borderRadius: 8 }}>{error}</div>}

        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={handleSubmit} disabled={saving} style={{ background: theme.buttonBackground, color: theme.buttonText, border: 'none', padding: '10px 14px', borderRadius: 8, fontWeight: 700 }}>{saving ? 'Saving...' : 'Create Chain'}</button>
          <button onClick={() => navigate(-1)} style={{ border: '1px solid ${theme.inputBorder}', background: theme.cardBackground, padding: '10px 14px', borderRadius: 8 }}>Cancel</button>
        </div>
      </div>
    </div>
  );
}


